import React, {Component} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DirectBillingObservationTypeEnum from "../../../enums/DirectBillingObservationTypeEnum";
import {FileUploader, SelectInput, TextareaInput, TextInput} from "../../../../../components";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import {base64ToBlob, downloadBlob} from "../../../../../utils/FileDownloader";
import ValidationUtil from "../../../../../components/form/validator/ValidationUtil";
import confirmAndDeleteDialog from "../../../../../components/dialog/ConfirmAndDeleteDialog";
import {FormField} from "../../../../../components/form";
import DirectBillingObservationFlagCodeEnum from "../../../enums/DirectBillingObservationFlagCodeEnum";
import dialog from "../../../../../components/dialog/Dialog";
import PayerTypeSelectionModal from "./PayerTypeSelectionModal";


export default class ClaimObservationsModal extends Component {

    constructor(props) {
        super(props);
        this.state = {
            observations: [],
            newObservation: {_addMode: true}
        };
    }

    componentDidMount() {
        this.retrieveObservations();
    }

    retrieveObservations() {
        RemotingService.getRemoteCall(`api/insurance/coordinator/claim-activity/${this.props.claimActivityId}/claim-activity-observation`, null, observations => {
            this.setState({observations});
        });
    }

    saveObservation(observation) {
        if (!ValidationUtil.checkWithNotification(this.formFields)) {
            return;
        }

        const params = {
            type: observation.type,
            code: observation.code,
            value: observation.type === "FILE" ? observation.fileValue.content : observation.value,
            filename: observation.type === "FILE" ? observation.fileValue.name : null,
            valueType: observation.valueType
        }

        RemotingService.postRemoteCall(`api/insurance/coordinator/claim-activity/observation/${this.props.claimActivityId}/save`, params, () => {
            this.retrieveObservations();
            this.setState({newObservation: {_addMode: true}});

            if (observation.type === "FLAG" &&
                (observation.code === "MedicalTourismUnplanned" || observation.code === "MedicalTourismPlanned") &&
                this.props.claimId) {
                this.showPayerTypeSelection();
            }
        });
    }

    showPayerTypeSelection() {
        dialog({
            title: "Medical Tourism Payer Type",
            component: <PayerTypeSelectionModal claimId={this.props.claimId}/>
        });
    }

    updateObservation(observation) {
        const params = {
            value: observation.type === "FILE" ? observation.fileValue.content : observation.value,
            filename: observation.type === "FILE" ? observation.fileValue.name : null,
            valueType: observation.valueType
        };

        RemotingService.postRemoteCall(`api/insurance/coordinator/claim-activity/observation/${observation.id}/update`, params, () => {
            this.retrieveObservations();
        });
    }

    deleteObservation(observation) {
        confirmAndDeleteDialog("Delete Observation", "Do you want to delete observation?",
            `api/insurance/coordinator/claim-activity/observation/${observation.id}`,
            () => { this.retrieveObservations(); });
    }

    editObservation(observation) {
        observation._editMode = true;
        if (observation.type === "FILE") {
            observation.fileValue = {
                content: observation.value,
                name: observation.filename,
                mimeType: "application/pdf"
            };
        }
        this.forceUpdate();
    }

    cancelEdit() {
        this.retrieveObservations();
    }

    getCode(observation) {
        switch (observation.type) {
            case "TEXT":
                return "Text"
            case "FILE":
                return "PDF"
            default:
                return null
        }
    }

    addFlagCodeTemplate(observation) {
        return (
            <FormField ref={formField => this.formFields.push(formField)}
                       required validateOn={observation.code}>
                <SelectInput searchEnabled={false}
                             displayProperty="name"
                             valueProperty="key"
                             className="font-italic"
                             options={DirectBillingObservationFlagCodeEnum.toArray()}
                             value={observation.code}
                             onChange={(code) => {
                                 observation.code = code

                                 this.forceUpdate();
                             }}/>
            </FormField>
        )
    }

    addTypeTemplate(observation) {
        return (
            <FormField ref={formField => this.formFields.push(formField)}
                       required validateOn={observation.type}>
                <SelectInput searchEnabled={false}
                             displayProperty="name"
                             valueProperty="key"
                             options={DirectBillingObservationTypeEnum.toArray()}
                             value={observation.type}
                             onChange={(type) => {
                                 observation.type = type;
                                 observation.value = null;
                                 observation.fileValue = null;
                                 observation.code = this.getCode(observation);

                                 this.forceUpdate();
                             }}/>
            </FormField>
        )
    }

    addValueTemplate(observation) {
        if (observation.type === "TEXT" || observation.type === "FLAG") {
            if (observation.value == null) {
                observation.value = ''
            }
            return (
                <FormField ref={formField => this.formFields.push(formField)}
                           required validateOn={observation.value} disabled={observation.type === "FLAG"}>
                    <TextareaInput className="w-100"
                                   value={observation.value}
                                   onChange={(value) => {
                                       observation.value = value;
                                       this.forceUpdate();
                                   }}/>
                </FormField>
            )
        } else if (observation.type === "FILE") {
            return (
                <FormField ref={formField => this.formFields.push(formField)}
                           required validateOn={observation.fileValue}>
                    <FileUploader basic accept=".pdf"
                                  value={observation.fileValue}
                                  onChange={(file) => {
                                      observation.fileValue = file;
                                      this.forceUpdate();
                                  }}/>
                </FormField>
            )
        }
    }

    addValueTypeTemplate(observation) {
        return (
            <FormField ref={formField => this.formFields.push(formField)}
                       required validateOn={observation.valueType} disabled={observation.type === "FLAG"}>
                <TextInput className="w-100"
                           value={observation.valueType}
                           onChange={(valueType) => {
                               observation.valueType = valueType;
                               this.forceUpdate();
                           }}/>
            </FormField>
        )
    }

    codeTemplate(observation) {
        return observation.type === "FLAG" ? DirectBillingObservationFlagCodeEnum[observation.code]?.name : observation.code;
    }

    typeTemplate(observation) {
        return DirectBillingObservationTypeEnum[observation.type]?.name;
    }

    valueTemplate(observation) {
        switch (observation.type) {
            case "TEXT":
            case "FLAG":
                return observation.value
            case "FILE":
                return <span className="cursor-pointer"
                             onClick={() => this.downloadFile(observation)}>
                <u>{observation.filename}</u>
                <FontAwesomeIcon className="ml-1" icon="file-download"/>
            </span>
            default:
                return null
        }
    }

    downloadFile(observation) {
        downloadBlob(base64ToBlob(observation.value, "application/pdf"), observation.filename);
    }

    valueTypeTemplate(observation) {
        return observation.valueType;
    }

    actionTemplate(observation) {
        if (observation._addMode) {
            return <FontAwesomeIcon icon="plus" onClick={() => this.saveObservation(observation)}/>
        } else if (observation._editMode) {
            return (
                <div>
                    <FontAwesomeIcon icon="check" onClick={() => this.updateObservation(observation)}/>
                    <FontAwesomeIcon className="ml-1" icon="times" onClick={() => this.cancelEdit(observation)}/>
                </div>
            )
        } else {
            return (
                <div>
                    <FontAwesomeIcon icon="edit" onClick={() => this.editObservation(observation)}/>
                    <FontAwesomeIcon className="ml-1" icon="trash" onClick={() => this.deleteObservation(observation)}/>
                </div>
            )
        }
    }

    render() {
        this.formFields = [];

        return (
            <div style={{width: 800}}>
                <DataTable emptyMessage={"No items found"}
                           value={[...this.state.observations, this.state.newObservation]}
                           columnResizeMode="fit"
                           dataKey="id">
                    <Column header="Type" style={{width: 100}}
                            body={(rowData) => rowData._addMode ? this.addTypeTemplate(rowData) : this.typeTemplate(rowData)}/>
                    <Column header="Code" style={{width: "35%", wordWrap: "break-word"}}
                            body={(rowData) => rowData._addMode && rowData.type === "FLAG" ? this.addFlagCodeTemplate(rowData) : this.codeTemplate(rowData)}/>
                    <Column header="Value"
                            body={(rowData) => (rowData._addMode || rowData._editMode) ? this.addValueTemplate(rowData) : this.valueTemplate(rowData)}/>
                    <Column header="Value Type" style={{width: 150}}
                            body={(rowData) => (rowData._addMode || rowData._editMode) ? this.addValueTypeTemplate(rowData) : this.valueTypeTemplate(rowData)}/>
                    <Column style={{width: 60, textAlign: "center"}}
                            body={(rowData) => this.actionTemplate(rowData)}/>
                </DataTable>
            </div>
        )
    }
}